import { useState, FormEvent } from "react";
import { useLocation } from "wouter";
import { GlassCard, Btn } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import { useAuthActions } from "@convex-dev/auth/react";
import { track } from "@/lib/telemetry";

/**
 * SignIn page for Move Nourish Flourish.
 *
 * Mode state machine:
 *   "signin"        email + password
 *   "signup"        email + password (creates account, then verify)
 *   "verify"        6-digit code from email (after sign up or unverified sign in)
 *   "forgot"        email only, sends reset code
 *   "reset"         code + new password
 *
 * Plus Google one-tap as a side-channel that bypasses verify entirely.
 */

type Mode = "signin" | "signup" | "verify" | "forgot" | "reset";

export default function SignIn() {
  const [, setLocation] = useLocation();
  const { signIn } = useAuthActions();

  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // ────────────────────────────────────────────────────────────────
  //  Handlers
  // ────────────────────────────────────────────────────────────────

  const onSubmitSignInOrUp = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setInfo("");
    const formData = new FormData(e.currentTarget);
    // Lowercased, not just trimmed. The auth library matches email
    // CASE-SENSITIVELY, so "Sister@x.com" and "sister@x.com" become two separate
    // accounts — and once two rows share an address its unique-user lookup
    // returns null and NOBODY with that address can sign in again. That is the
    // lockout, and it is self-amplifying.
    const submittedEmail = (formData.get("email") as string)?.trim().toLowerCase();
    const password = formData.get("password") as string;
    if (!submittedEmail || !submittedEmail.includes("@")) {
      setError("Please enter a valid email.");
      return;
    }
    if (!password || password.length < 8) {
      setError("Password needs to be at least 8 characters.");
      return;
    }
    const convexFlow = mode === "signin" ? "signIn" : "signUp"; formData.set("flow", convexFlow);
    // The normalized address must go to the SERVER, not just into a local
    // variable — signIn() reads formData, so without this the raw casing is
    // still what gets matched and stored.
    formData.set("email", submittedEmail);
    setBusy(true);
    track("auth_password_submit", { flow: convexFlow });
    try {
      await signIn("password", formData);
      // If sign up flow: server now expects a verification code.
      // If sign in flow with verified email: signed in.
      // If sign in flow with UNverified email: server returns and we move to verify.
      setEmail(submittedEmail);
      if (mode === "signup") {
        setMode("verify");
        setInfo(`We sent a 6-digit code to ${submittedEmail}. Check your inbox.`);
      }
      // For signin, if it didn't throw, GatedRoutes will swap us to the app.
    } catch (err: any) {
      const msg = humanizeAuthError(err, mode);
      setError(msg);
      // If server told us "verify your email", flip to verify mode.
      if (msg.toLowerCase().includes("verify")) {
        setEmail(submittedEmail);
        setMode("verify");
        setInfo(`Your email isn't verified yet. We just sent a fresh 6-digit code to ${submittedEmail}.`);
      }
    } finally {
      setBusy(false);
    }
  };

  const onSubmitVerify = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    const formData = new FormData(e.currentTarget);
    formData.set("flow", "email-verification");
    formData.set("email", email.trim().toLowerCase());
    const code = (formData.get("code") as string)?.trim();
    if (!code || code.length < 6) {
      setError("Enter the 6-digit code from the email.");
      return;
    }
    setBusy(true);
    track("auth_verify_submit");
    try {
      await signIn("password", formData);
      // GatedRoutes will swap us to the app.
    } catch (err: any) {
      setError(humanizeAuthError(err, "verify"));
    } finally {
      setBusy(false);
    }
  };

  const onSubmitForgot = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setInfo("");
    const formData = new FormData(e.currentTarget);
    const submittedEmail = (formData.get("email") as string)?.trim();
    if (!submittedEmail || !submittedEmail.includes("@")) {
      setError("Please enter the email on your account.");
      return;
    }
    formData.set("flow", "reset");
    setBusy(true);
    track("auth_reset_request");
    try {
      await signIn("password", formData);
      setEmail(submittedEmail);
      setMode("reset");
      setInfo(`We sent a reset code to ${submittedEmail}. It expires in 15 minutes.`);
    } catch (err: any) {
      setError(humanizeAuthError(err, "forgot"));
    } finally {
      setBusy(false);
    }
  };

  const onSubmitReset = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    const formData = new FormData(e.currentTarget);
    formData.set("flow", "reset-verification");
    formData.set("email", email.trim().toLowerCase());
    const code = (formData.get("code") as string)?.trim();
    const newPassword = formData.get("newPassword") as string;
    if (!code || code.length < 6) {
      setError("Enter the 6-digit code from the email.");
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      setError("New password needs to be at least 8 characters.");
      return;
    }
    setBusy(true);
    track("auth_reset_submit");
    try {
      await signIn("password", formData);
      // GatedRoutes will swap us to the app.
    } catch (err: any) {
      setError(humanizeAuthError(err, "reset"));
    } finally {
      setBusy(false);
    }
  };

  const onGoogle = async () => {
    setError("");
    track("auth_google_clicked");
    try {
      await signIn("google");
    } catch (err: any) {
      setError(err?.message || "Google sign-in failed. Please try again.");
    }
  };

  // ────────────────────────────────────────────────────────────────
  //  Render helpers
  // ────────────────────────────────────────────────────────────────

  const headerTitle =
    mode === "signin"
      ? "Sign in"
      : mode === "signup"
      ? "Create your account"
      : mode === "verify"
      ? "Verify your email"
      : mode === "forgot"
      ? "Reset your password"
      : "Set a new password";

  const headerBlurb =
    mode === "signin"
      ? "Welcome back, sister. Sign in to your garden."
      : mode === "signup"
      ? "Tend something beautiful. We'll send a verification code to your email."
      : mode === "verify"
      ? "Type the 6-digit code we just emailed you."
      : mode === "forgot"
      ? "Tell us the email on your account and we'll send a reset code."
      : "Type the code from your inbox and choose a new password.";

  return (
    <div style={{ minHeight: "100vh", padding: "32px 22px 80px", background: "var(--mnf-cream)" }}>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 24, position: "relative" }}>
        <button
          onClick={() => {
            if (mode === "verify" || mode === "reset") {
              // step back inside the auth flow
              setMode(mode === "verify" ? "signup" : "forgot");
              setError("");
              setInfo("");
            } else if (mode === "forgot") {
              setMode("signin");
              setError("");
              setInfo("");
            } else {
              setLocation("/");
            }
          }}
          data-testid="button-back"
          className="hover-elevate"
          aria-label="Back"
          style={{
            // 44px tap target (was 36px) — the icon itself stays the same
            // visual size, only the tappable area grows.
            position: "absolute", left: 0, top: -4,
            width: 44, height: 44, borderRadius: 999,
            background: "rgba(30,74,53,0.08)", color: "var(--mnf-f)",
            border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <Icon name="arrow_back" size={18} />
        </button>
        <h1 className="mnf-serif" style={{ fontSize: 22, color: "var(--mnf-fd)", fontWeight: 500 }}>
          {headerTitle}
        </h1>
      </div>

      <p
        className="mnf-body"
        style={{ color: "var(--mnf-tg)", fontSize: 13.5, lineHeight: 1.55, textAlign: "center", marginBottom: 22 }}
      >
        {headerBlurb}
      </p>

      {/* ── Sign in / Sign up tabs ────────────────────────────── */}
      {(mode === "signin" || mode === "signup") && (
        <>
          <ModeTabs mode={mode} onChange={(m) => { setMode(m); setError(""); setInfo(""); }} />

          <GlassCard padding={18}>
            <form onSubmit={onSubmitSignInOrUp}>
              <FieldLabel>Email</FieldLabel>
              <Input name="email" type="email" placeholder="you@yours.com" autoComplete="email" />
              <div style={{ height: 14 }} />
              <FieldLabel>Password</FieldLabel>
              <Input
                name="password"
                type="password"
                placeholder={mode === "signup" ? "At least 8 characters" : "Your password"}
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
              />
              {error && <ErrorText>{error}</ErrorText>}
              {info && <InfoText>{info}</InfoText>}
              <div className="mt-4">
                <Btn variant="primary" block size="lg" disabled={busy} type="submit">
                  <Icon name="lock" size={18} />{" "}
                  {busy
                    ? mode === "signup" ? "Creating..." : "Signing in..."
                    : mode === "signup" ? "Create account" : "Sign in"}
                </Btn>
              </div>
              {mode === "signin" && (
                <button
                  type="button"
                  onClick={() => { setMode("forgot"); setError(""); setInfo(""); }}
                  className="mnf-body"
                  style={{
                    // 44px tap target (was 19px) — this is the account-recovery
                    // link, so it can't be the thing that's too small to tap.
                    // Text size/weight untouched; only the hit area grows via
                    // minHeight + flex centering.
                    background: "none", border: "none", padding: "0", marginTop: 4,
                    minHeight: 44, width: "100%",
                    display: "flex", alignItems: "center", justifyContent: "flex-start",
                    color: "var(--mnf-f)", fontSize: 12.5, cursor: "pointer", textDecoration: "underline",
                  }}
                >
                  Forgot password?
                </button>
              )}
            </form>
          </GlassCard>

          <Divider />
          <GoogleButton onClick={onGoogle} />
        </>
      )}

      {/* ── Verify email after sign up ─────────────────────────── */}
      {mode === "verify" && (
        <GlassCard padding={18}>
          <form onSubmit={onSubmitVerify}>
            <FieldLabel>6-digit code</FieldLabel>
            <Input
              name="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              maxLength={6}
            />
            {info && <InfoText>{info}</InfoText>}
            {error && <ErrorText>{error}</ErrorText>}
            <div className="mt-4">
              <Btn variant="primary" block size="lg" disabled={busy} type="submit">
                <Icon name="check" size={18} /> {busy ? "Verifying..." : "Verify and sign in"}
              </Btn>
            </div>
            <button
              type="button"
              onClick={() => { setMode("signup"); setError(""); setInfo(""); }}
              className="mnf-body"
              style={{
                background: "none", border: "none", padding: 0, marginTop: 12,
                color: "var(--mnf-f)", fontSize: 12.5, cursor: "pointer", textDecoration: "underline",
              }}
            >
              Use a different email
            </button>
          </form>
        </GlassCard>
      )}

      {/* ── Forgot password (request code) ─────────────────────── */}
      {mode === "forgot" && (
        <GlassCard padding={18}>
          <form onSubmit={onSubmitForgot}>
            <FieldLabel>Email</FieldLabel>
            <Input name="email" type="email" placeholder="you@yours.com" autoComplete="email" defaultValue={email} />
            {info && <InfoText>{info}</InfoText>}
            {error && <ErrorText>{error}</ErrorText>}
            <div className="mt-4">
              <Btn variant="primary" block size="lg" disabled={busy} type="submit">
                <Icon name="mail" size={18} /> {busy ? "Sending..." : "Send reset code"}
              </Btn>
            </div>
            <button
              type="button"
              onClick={() => { setMode("signin"); setError(""); setInfo(""); }}
              className="mnf-body"
              style={{
                background: "none", border: "none", padding: 0, marginTop: 12,
                color: "var(--mnf-f)", fontSize: 12.5, cursor: "pointer", textDecoration: "underline",
              }}
            >
              Back to sign in
            </button>
          </form>
        </GlassCard>
      )}

      {/* ── Reset password (verify code + new password) ────────── */}
      {mode === "reset" && (
        <GlassCard padding={18}>
          <form onSubmit={onSubmitReset}>
            <FieldLabel>6-digit code</FieldLabel>
            <Input
              name="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              maxLength={6}
            />
            <div style={{ height: 14 }} />
            <FieldLabel>New password</FieldLabel>
            <Input name="newPassword" type="password" placeholder="At least 8 characters" autoComplete="new-password" />
            {info && <InfoText>{info}</InfoText>}
            {error && <ErrorText>{error}</ErrorText>}
            <div className="mt-4">
              <Btn variant="primary" block size="lg" disabled={busy} type="submit">
                <Icon name="check" size={18} /> {busy ? "Saving..." : "Save and sign in"}
              </Btn>
            </div>
          </form>
        </GlassCard>
      )}

      <p
        className="mnf-body"
        style={{ fontSize: 11, color: "var(--mnf-tg)", marginTop: 26, textAlign: "center", lineHeight: 1.55 }}
      >
        By signing in you agree to our Terms and Privacy.
      </p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
//  Small presentational helpers
// ────────────────────────────────────────────────────────────────

function ModeTabs({ mode, onChange }: { mode: "signin" | "signup"; onChange: (m: "signin" | "signup") => void }) {
  const tab = (key: "signin" | "signup", label: string) => (
    <button
      key={key}
      type="button"
      onClick={() => onChange(key)}
      className="mnf-body"
      aria-pressed={mode === key}
      style={{
        flex: 1,
        // 44px tap target (was 40px) via minHeight; padding/font untouched.
        minHeight: 44,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "10px 12px",
        borderRadius: 999,
        border: "none",
        background: mode === key ? "var(--mnf-f)" : "transparent",
        color: mode === key ? "#fff" : "var(--mnf-fd)",
        fontWeight: 600,
        fontSize: 13,
        cursor: "pointer",
        transition: "background 160ms ease",
      }}
    >
      {label}
    </button>
  );
  return (
    <div
      style={{
        display: "flex",
        gap: 4,
        background: "rgba(30,74,53,0.08)",
        borderRadius: 999,
        padding: 4,
        marginBottom: 14,
      }}
    >
      {tab("signin", "Sign in")}
      {tab("signup", "Sign up")}
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="mnf-eyebrow block mb-2" style={{ color: "var(--mnf-tg)" }}>
      {children}
    </label>
  );
}

function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className="mnf-body w-full"
      style={{
        padding: "12px 14px",
        borderRadius: 12,
        border: "1px solid rgba(30,74,53,0.18)",
        background: "rgba(255,255,255,0.65)",
        color: "var(--mnf-fd)",
        fontSize: 14,
        outline: "none",
      }}
    />
  );
}

function ErrorText({ children }: { children: React.ReactNode }) {
  return (
    <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-t)", marginTop: 10, lineHeight: 1.5 }}>
      {children}
    </p>
  );
}

function InfoText({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mnf-body"
      style={{
        marginTop: 10, padding: 12, borderRadius: 10,
        background: "rgba(168,212,184,0.25)",
        color: "var(--mnf-fd)", fontSize: 12.5, lineHeight: 1.55,
      }}
    >
      {children}
    </div>
  );
}

function Divider() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "22px 0" }}>
      <div style={{ flex: 1, height: 1, background: "rgba(30,74,53,0.15)" }} />
      <span className="mnf-eyebrow" style={{ color: "var(--mnf-tg)" }}>or</span>
      <div style={{ flex: 1, height: 1, background: "rgba(30,74,53,0.15)" }} />
    </div>
  );
}

function GoogleButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      data-testid="button-google-signin"
      onClick={onClick}
      className="mnf-body w-full hover-elevate"
      style={{
        padding: "13px 16px",
        borderRadius: 12,
        background: "#fff",
        color: "#1f1f1f",
        border: "1px solid rgba(30,74,53,0.18)",
        fontSize: 14,
        fontWeight: 600,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
        cursor: "pointer",
      }}
    >
      <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
        <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
        <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
        <path fill="#FBBC05" d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.96H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.04l3.007-2.333z"/>
        <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.96L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/>
      </svg>
      Continue with Google
    </button>
  );
}

function humanizeAuthError(err: any, mode: Mode): string {
  const raw = (err?.message || "").toString();
  // Strip the "[CONVEX A(...)] [Request ID: ...] Server Error Uncaught Error: " prefix
  // that Convex adds to server-side throws so users never see internal stack traces.
  const clean = raw
    .replace(/\[CONVEX [^\]]+\]\s*/g, "")
    .replace(/\[Request ID:[^\]]+\]\s*/g, "")
    .replace(/Server Error\s*/g, "")
    .replace(/Uncaught Error:\s*/g, "")
    .split(/\s+at\s+/)[0] // strip stack trace
    .trim();
  const lower = clean.toLowerCase();

  if (lower.includes("invalidaccountid") || lower.includes("account") && lower.includes("not found")) {
    if (mode === "forgot" || mode === "reset") {
      return "No password account found for that email. If you signed in with Google, use Google to sign in instead. Or create a new account.";
    }
    return "That email and password don't match. Try again or reset your password.";
  }
  if (lower.includes("invalid credentials") || lower.includes("invalid password") || lower.includes("invalidsecret")) {
    if (mode === "signin") return "That email and password don't match. Try again or reset your password.";
    if (mode === "verify" || mode === "reset") return "That code doesn't match. Double-check the latest email.";
  }
  if (lower.includes("user already exists") || lower.includes("account already exists")) {
    return "An account with that email already exists. Try signing in instead.";
  }
  if (lower.includes("password") && lower.includes("8")) {
    return "Password needs to be at least 8 characters.";
  }
  if (lower.includes("expired")) {
    return "That code expired. Send a new one and try again.";
  }
  if (lower.includes("rate") || lower.includes("too many")) {
    return "Too many attempts. Give it a minute and try again.";
  }
  return clean || "Something went wrong. Please try again.";
}
